import { LegalPageLayout } from '../components/LegalPageLayout'
import type { TocItem } from '../components/TableOfContents'

const tocItems: TocItem[] = [
  { id: 'section-1', title: '1. Definitions' },
  { id: 'section-2', title: '2. What Benepicks Is and Is Not' },
  { id: 'section-3', title: '3. Accounts and Access' },
  { id: 'section-4', title: '4. Acceptable Use' },
  { id: 'section-5', title: '5. Data and Privacy' },
  { id: 'section-6', title: '6. Intellectual Property' },
  { id: 'section-7', title: '7. Automated Features' },
  { id: 'section-8', title: '8. Users Without a User Agreement' },
  { id: 'section-9', title: '9. Modifications' },
  { id: 'section-10', title: '10. General' },
]

export const Terms = () => {
  return (
    <LegalPageLayout
      title="Platform Terms"
      lastUpdated="October 5, 2026"
      tocItems={tocItems}
    >
      <section id="introduction" className="mb-10">
        <p>
          These Benepicks Platform Terms (the “Platform Terms”) govern access to
          and use of the Benepicks platform, including its websites, mobile
          applications, APIs, dashboards, and related technology (the
          “Platform”), which is provided by Benepicks, Inc., a Delaware
          corporation (“Benepicks,” “we,” or “us”). By accessing or using the
          Platform, you agree to these Platform Terms, the Benepicks Privacy
          Policy, and the Benepicks Electronic Communications Consent, each of
          which is incorporated by reference.
        </p>
        <p>
          Your commercial relationship with Benepicks is governed by your User
          Agreement: the Benepicks Master Services Agreement if you are an
          Employer, or the Benepicks Member Terms of Service if you are a
          Member. Your User Agreement governs the scope of the Services, fees,
          warranties and disclaimers, limitations of liability, indemnification,
          and dispute resolution, including any arbitration agreement and class
          action waiver. These Platform Terms govern how the Platform may be
          accessed and used. If you use the Platform without a User Agreement
          (for example, a quoting tool on our website), Section 8 applies to
          you.
        </p>
      </section>
      <section id="section-1" className="mb-10">
        <h2>1. Definitions</h2>
        <p>
          <strong>“Applicable Law”</strong> means any federal, state, or local
          law, regulation, order, or rule that governs the use of the Platform,
          the operation of any Plan, health coverage, the handling of personal
          information and Protected Health Information, or electronic
          communications.
        </p>
        <p>
          <strong>“Benepicks IP”</strong> has the meaning given in Section 6.1.
        </p>
        <p>
          <strong>“Dependent”</strong> means a spouse, child, or other
          individual whose information a Member provides to the Platform or who
          is enrolled in or reimbursed for coverage through a Member. Dependents
          do not use the Platform and are not parties to these Platform Terms. A
          Member who provides information about a Dependent represents that the
          Member is authorized to do so and to act for the Dependent in
          connection with the Platform.
        </p>
        <p>
          <strong>“ERISA”</strong> means the Employee Retirement Income Security
          Act of 1974, as amended.
        </p>
        <p>
          <strong>“Employer”</strong> means an entity that has engaged Benepicks
          under a Master Services Agreement.
        </p>
        <p>
          <strong>“Member”</strong> means an employee of an Employer who
          accesses or uses the Platform.
        </p>
        <p>
          <strong>“PHI”</strong> means Protected Health Information as defined
          under the Health Insurance Portability and Accountability Act of 1996
          and its implementing regulations (HIPAA).
        </p>
        <p>
          <strong>“Plan”</strong> means a Custom Health Option and Individual
          Care Expense Arrangement (CHOICE Arrangement, formerly known as an
          Individual Coverage Health Reimbursement Arrangement or ICHRA) under
          26 C.F.R. § 54.9802-4, a cafeteria plan under Section 125 of the
          Internal Revenue Code, or any other employee welfare benefit
          arrangement sponsored by an Employer in connection with which the
          Platform is used.
        </p>
        <p>
          <strong>“Services”</strong> means the software, technology, and
          operational support services Benepicks provides through the Platform,
          as described in the applicable User Agreement.
        </p>
        <p>
          <strong>“User Agreement”</strong> means the Master Services Agreement
          or the Member Terms of Service, as applicable. The Broker Partner
          Agreement is not a User Agreement and is governed by its own terms.
        </p>
        <p>
          Capitalized terms used but not defined in these Platform Terms have
          the meanings given in the applicable User Agreement.
        </p>
      </section>
      <section id="section-2" className="mb-10">
        <h2>2. What Benepicks Is and Is Not</h2>
        <p>
          <strong>2.1 Technology Platform; Ministerial Role.</strong> Benepicks
          is a technology platform. Its role is ministerial and
          non-discretionary. Benepicks does not exercise or accept any
          discretionary authority or control over the management or
          administration of any Plan or the disposition of Plan assets.
          Benepicks is not a “plan administrator” within the meaning of ERISA §
          3(16), a “fiduciary” within the meaning of ERISA § 3(21), a named
          fiduciary, a plan sponsor, a third-party administrator, a professional
          employer organization, or a claims administrator. Where the Platform
          is used in connection with an Employer’s Plan, the Employer is and
          remains the sole plan sponsor, plan administrator, and named fiduciary
          of that Plan and is solely responsible for Plan-level compliance with
          Applicable Law.
        </p>
        <p>
          <strong>2.2 Licensed Insurance Activity.</strong> Benepicks, Inc. is
          not a licensed insurance agency. Insurance solicitation,
          recommendation, advice, quoting, and enrollment for individual
          policies are performed by duly licensed insurance producers acting as
          Agent of Record, and not by Benepicks, Inc. in its capacity as
          platform provider. Those producers are independent of Benepicks and
          responsible for their own licensing and conduct. Producers acting as
          Agent of Record are paid commissions by the insurance carrier for the
          policies they place; Benepicks, Inc. receives no insurance
          commissions. Recourse for any matter relating to the placement of,
          claims under, or carrier disputes regarding any policy lies solely
          against the applicable producer or carrier.
        </p>
        <p>
          <strong>2.3 Not a Bank or Money Transmitter.</strong> Benepicks is not
          a bank, money transmitter, or FDIC-insured depository institution. All
          movement of funds is performed by a licensed money transmitter and its
          bank partners under their own terms. Benepicks does not hold or take
          title to any Employer or Member funds and does not earn float or
          interest on them. Benepicks may receive payment-processing
          compensation from its money movement partner or that partner’s banking
          and card-network partners, as disclosed in the applicable User
          Agreement. Benepicks is not responsible for the acts or omissions of
          any party in the payment chain.
        </p>
        <p>
          <strong>2.4 Not the Health Insurance Marketplace.</strong> Benepicks
          is not the Health Insurance Marketplace® at HealthCare.gov, any
          state-based exchange, the Centers for Medicare &amp; Medicaid
          Services, or any other governmental entity, and is not registered as a
          web-broker entity under 45 C.F.R. § 155.220. The Platform may display
          information about coverage available in your area, but Benepicks does
          not represent that it reflects every plan available to you. For a
          complete list of Qualified Health Plans, visit HealthCare.gov or your
          state’s exchange.
        </p>
        <p>
          <strong>2.5 No Advice.</strong> Materials and outputs furnished
          through the Platform are general informational content. Benepicks does
          not provide legal, tax, accounting, actuarial, investment, insurance,
          medical, or fiduciary advice. You are solely responsible for obtaining
          your own professional advice and shall not rely on Benepicks for it.
        </p>
        <p>
          <strong>2.6 Not for Emergencies.</strong> The Platform is not a
          substitute for emergency care. If you are experiencing a medical or
          mental health emergency, call 911, go to the nearest emergency room,
          or contact the 988 Suicide and Crisis Lifeline.
        </p>
        <p>
          <strong>2.7 Licensed Personnel; Future Services.</strong> Benepicks
          personnel may include licensed insurance producers. Any consultative
          input they provide is part of Benepicks’ technology services, is
          informational only, and does not make Benepicks a fiduciary, an
          insurance agency, or the Agent of Record for any policy. Any future
          advisory, consulting, or brokerage services offered by Benepicks or an
          affiliate will be governed by a separate written agreement.
        </p>
      </section>
      <section id="section-3" className="mb-10">
        <h2>3. Accounts and Access</h2>
        <p>
          <strong>3.1 Eligibility.</strong> To create an account you must be at
          least eighteen (18) years old, be a U.S. resident (or, for Employer
          accounts, a U.S. business entity acting through an authorized
          representative), and have the legal capacity and authority to enter
          into the applicable User Agreement. You represent that you are not
          subject to U.S. economic sanctions and will not use the Platform in
          violation of sanctions or export laws.
        </p>
        <p>
          <strong>3.2 Credentials and Authorized Persons.</strong> You are
          responsible for safeguarding your credentials and for all activity
          under your account. Do not share your credentials. Notify Benepicks
          promptly of any suspected unauthorized access. Benepicks may rely on
          any action taken through your account, including by any person you
          designate to act for you (an “Authorized Person”), and you are
          responsible for the acts and omissions of each Authorized Person.
          Revocation of an Authorized Person’s access takes effect when
          Benepicks has processed it.
        </p>
        <p>
          <strong>3.3 Identity Verification.</strong> Benepicks and its partners
          may request information needed to verify your identity, authority,
          eligibility, or licensure, before or after opening your account, and
          may use third-party providers to verify it. Benepicks may limit access
          until verification is complete.
        </p>
        <p>
          <strong>3.4 Accurate Information.</strong> You shall provide accurate,
          complete, and current information and update it promptly when it
          changes. You are solely responsible for the consequences of
          inaccurate, incomplete, or untimely information, including incorrect
          quotes, missed enrollments, denied claims, regulatory penalties, and
          adverse tax consequences.
        </p>
        <p>
          <strong>3.5 Plan Eligibility Decisions.</strong> Eligibility to
          participate in any Plan, and whether any expense is reimbursable, is
          determined under the Employer’s Plan documents and Applicable Law by
          the Employer or Plan administrator (and, where applicable, the
          carrier), not by Benepicks.
        </p>
        <p>
          <strong>3.6 Suspension and Termination.</strong> Benepicks may suspend
          or terminate access to the Platform, with or without notice, if you
          breach these Platform Terms or your User Agreement; if Benepicks
          reasonably suspects fraud, misuse, or unlawful conduct; if the
          applicable Employer’s engagement with Benepicks ends; or if required
          by law, a regulator, a carrier, or a money movement, banking,
          card-network, or insurance partner. Benepicks may decline, condition,
          or terminate service to any Employer or user that is engaged in
          activity Benepicks determines to be unlawful or that its partners
          prohibit, that is subject to sanctions, or that operates in an
          industry Benepicks’ partners restrict or that Benepicks designates
          under the applicable User Agreement, and may request additional
          information at onboarding based on industry or risk profile.
        </p>
      </section>
      <section id="section-4" className="mb-10">
        <h2>4. Acceptable Use</h2>
        <p>
          You may use the Platform only to enroll in, elect, pay for,
          administer, or support the benefits described in your User Agreement.
          You shall not, and shall not permit anyone else to:
        </p>
        <ul>
          <li>
            copy, modify, or create derivative works of the Platform or any
            Benepicks IP;
          </li>
          <li>
            reverse engineer, decompile, or disassemble any part of the
            Platform;
          </li>
          <li>
            sell, resell, sublicense, rent, or commercially exploit the
            Services;
          </li>
          <li>
            use the Services to build a competing product, perform benchmarking,
            or train any model;
          </li>
          <li>
            access the Platform through bots, scrapers, or other automated means
            not expressly authorized;
          </li>
          <li>
            interfere with the security, integrity, or performance of the
            Platform;
          </li>
          <li>
            upload content that is unlawful, infringing, or that you do not have
            the right to provide;
          </li>
          <li>impersonate any person or misrepresent your authority; or</li>
          <li>
            use the Platform in violation of Applicable Law or any third-party
            right.
          </li>
        </ul>
      </section>
      <section id="section-5" className="mb-10">
        <h2>5. Data and Privacy</h2>
        <p>
          <strong>5.1 Privacy Policy.</strong> Benepicks collects, uses, and
          discloses personal information as described in the Benepicks Privacy
          Policy.
        </p>
        <p>
          <strong>5.2 HIPAA.</strong> To the extent Benepicks creates, receives,
          maintains, or transmits PHI on behalf of an Employer’s group health
          plan, it does so as a HIPAA Business Associate under the Business
          Associate Agreement with that Employer. A licensed producer or other
          partner that handles PHI does so either as a Subcontractor Business
          Associate of Benepicks under a written agreement or as the Employer’s
          plan’s own business associate under its own agreement, as the
          applicable User Agreement provides. The Business Associate Agreement
          controls over these Platform Terms as to PHI.
        </p>
        <p>
          <strong>5.3 Your Data.</strong> The rights Benepicks has in Employer
          data are set forth in the Master Services Agreement. The consents
          Members give to share their information, including information about
          their Dependents, are set forth in the Member Terms of Service and the
          Privacy Policy.
        </p>
        <p>
          <strong>5.4 Security.</strong> Benepicks maintains reasonable
          administrative, physical, and technical safeguards designed to protect
          the information it processes, appropriate to the nature of that
          information and the risks involved.
        </p>
      </section>
      <section id="section-6" className="mb-10">
        <h2>6. Intellectual Property</h2>
        <p>
          <strong>6.1 Benepicks IP.</strong> Benepicks and its licensors own all
          right, title, and interest in the Platform, the Services, and all
          related software, content, documentation, trademarks, and intellectual
          property (“Benepicks IP”). Subject to your compliance with these
          Platform Terms and your User Agreement, Benepicks grants you a
          limited, non-exclusive, non-transferable, revocable right to access
          and use the Platform as permitted by your User Agreement. No other
          rights are granted, by implication or otherwise.
        </p>
        <p>
          <strong>6.2 Feedback.</strong> If you provide suggestions or feedback
          about the Platform, Benepicks may use them without restriction or
          obligation to you.
        </p>
      </section>
      <section id="section-7" className="mb-10">
        <h2>7. Automated Features</h2>
        <p>
          Benepicks may incorporate artificial intelligence, machine learning,
          recommendation engines, chatbots, or other automated processing into
          the Services (“Automated Features”). Automated Features are provided
          “AS IS” as an informational aid. Their outputs are probabilistic, may
          be inaccurate, and are not legal, tax, insurance, medical, or
          fiduciary advice. You shall review and validate outputs before relying
          on them and are solely responsible for decisions based on them.
          Benepicks disclaims all liability arising from Automated Features to
          the maximum extent permitted by law.
        </p>
      </section>
      <section id="section-8" className="mb-10">
        <h2>8. Users Without a User Agreement</h2>
        <p>
          If you use the Platform without a User Agreement, the Platform is
          provided “AS IS” and “AS AVAILABLE,” and Benepicks disclaims all
          warranties, express, implied, or statutory, to the maximum extent
          permitted by law. Benepicks will not be liable to you for any
          indirect, incidental, special, consequential, or punitive damages, and
          Benepicks’ total liability to you arising out of your use of the
          Platform will not exceed one hundred U.S. dollars ($100). Any quote,
          estimate, or comparison displayed to you is an illustration based on
          information you provide and is not an offer of coverage or a guarantee
          of price or availability. These Platform Terms are governed by the
          laws of the State of Delaware, and the state and federal courts
          located in Wilmington, Delaware have exclusive jurisdiction over any
          dispute, except that mandatory consumer protections of your state of
          residence that cannot be waived by contract still apply.
        </p>
      </section>
      <section id="section-9" className="mb-10">
        <h2>9. Modifications</h2>
        <p>
          <strong>9.1 Changes to the Services.</strong> Benepicks may expand,
          enhance, or modify the Services from time to time. Your use of any new
          or modified Services constitutes acceptance of these Platform Terms as
          applied to them. Protection against material degradation of ordered
          functionality during a current term is addressed in the applicable
          User Agreement.
        </p>
        <p>
          <strong>9.2 Changes to These Platform Terms.</strong> Benepicks may
          modify these Platform Terms. The “Last Updated” date reflects the most
          recent revision. Benepicks will notify you of material changes by
          email or a notice in the Platform before they take effect.
          Non-material changes take effect on posting. If you do not agree to a
          material change, your sole remedy is to stop using the Platform and
          close your account before the change takes effect; continued use after
          the effective date is acceptance. Any dispute is governed by the
          version of these Platform Terms and your User Agreement in effect when
          the dispute first arose, and modifications to any dispute resolution
          provision do not apply to a dispute of which Benepicks had notice
          before the change took effect. Prior versions are available by writing
          to legal@benepicks.com.
        </p>
      </section>
      <section id="section-10" className="mb-10">
        <h2>10. General</h2>
        <p>
          <strong>10.1 Order of Precedence.</strong> In the event of conflict:
          (a) the Business Associate Agreement controls as to PHI and HIPAA
          obligations; (b) your User Agreement controls as to all commercial
          terms, including scope of Services, fees, warranties, liability,
          indemnification, and dispute resolution; (c) these Platform Terms
          control as to access to and use of the Platform; and (d) no
          pre-printed terms in any purchase order or similar document have any
          effect.
        </p>
        <p>
          <strong>10.2 Relationship.</strong> The parties are independent
          contractors. Nothing in these Platform Terms creates a partnership,
          joint venture, agency, fiduciary, employment, joint employer, or
          co-employment relationship.
        </p>
        <p>
          <strong>10.3 No Third-Party Beneficiaries.</strong> Except as provided
          in your User Agreement or the Business Associate Agreement, these
          Platform Terms are for the benefit of you and Benepicks only.
        </p>
        <p>
          <strong>10.4 Assignment.</strong> You may not assign or transfer these
          Platform Terms without Benepicks’ prior written consent. Benepicks may
          assign them, including to an affiliate or successor or in connection
          with a financing, merger, acquisition, or sale of assets.
        </p>
        <p>
          <strong>10.5 Notices.</strong> Notices to Benepicks regarding these
          Platform Terms shall be sent to legal@benepicks.com or by mail to
          Benepicks, Inc., 1037 NE 65th St, Ste 82579, Seattle, WA 98115.
          Notices to you may be sent to the email address associated with your
          account, by in-Platform notification, or by posting on the Platform.
        </p>
        <p>
          <strong>10.6 Severability; Waiver; Construction.</strong> If any
          provision is held unenforceable, it shall be modified to the minimum
          extent necessary and the remaining provisions remain in effect. No
          waiver is effective unless in writing. Headings are for convenience
          only, “including” means “including without limitation,” and
          ambiguities shall not be construed against the drafter.
        </p>
        <p>
          <strong>10.7 Force Majeure.</strong> Benepicks is not liable for any
          delay or failure caused by events beyond its reasonable control,
          including acts of God, war, terrorism, civil unrest, pandemic,
          governmental action, labor disputes, cyberattacks, internet or
          telecommunications failures, or failures of third-party services.
        </p>
        <p>
          <strong>10.8 Survival.</strong> Sections 2, 5, 6, 7, 8, 9.2, and 10,
          and any provision that by its nature should survive, survive
          termination of your account or User Agreement.
        </p>
        <p>
          <strong>10.9 California Consumer Complaints.</strong> California
          residents may report complaints to the Complaint Assistance Unit of
          the Division of Consumer Services of the California Department of
          Consumer Affairs, 1625 North Market Blvd., Suite N 112, Sacramento, CA
          95834, (800) 952-5210.
        </p>
      </section>
    </LegalPageLayout>
  )
}
